<template>
  <section id="features-hero" class="hero-fullscreen" :style="heroBgVars">
    <div class="hero-bg" :style="bgStyle"></div>
    <div class="hero-gradient"></div>
    <div v-if="titleHtml || title || titleGrey || $slots.default" class="hero-title-block">
      <slot>
        <h1 v-if="titleHtml" class="hero-title tt-up p hero-title-html" v-html="titleHtml"></h1>
        <h1 v-else class="hero-title tt-up p">
          <span v-if="displayedTitle.length" class="hero-title-white">{{ displayedTitle }}</span><span v-if="displayedTitleGrey.length" class="hero-title-grey">{{ displayedTitleGrey }}</span><span class="hero-cursor" v-if="showCursor">|</span>
        </h1>
      </slot>
    </div>
    <div class="arrow-block">
      <div class="ani-arrow d-f ai-c jc-c">
        <IconBase w="15" h="15" color="white"><IconArrowDown /></IconBase>
      </div>
    </div>
  </section>
</template>

<script>
import IconBase from '@/assets/icons/IconBase.vue'
import IconArrowDown from '@/assets/icons/IconArrowDown.vue'

export default {
  name: 'FeaturesHero',
  components: {
    IconBase,
    IconArrowDown,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    titleGrey: {
      type: String,
      default: '',
    },
    titleHtml: {
      type: String,
      default: '',
    },
    backgroundImage: {
      type: String,
      default: '',
    },
    backgroundImageMobile: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      parallaxOffset: 0,
      scrollTicking: false,
      displayedLength: 0,
      typewriterTimer: null,
      typingSpeed: 50,
    }
  },
  computed: {
    fullText() {
      const white = this.title || ''
      const grey = this.titleGrey ? ' ' + this.titleGrey : ''
      return white + grey
    },
    displayedTitle() {
      const len = Math.min(this.displayedLength, (this.title || '').length)
      return (this.title || '').substring(0, len)
    },
    displayedTitleGrey() {
      const titleLen = (this.title || '').length
      if (this.displayedLength <= titleLen || !this.titleGrey) return ''
      return ' ' + this.titleGrey.substring(0, this.displayedLength - titleLen - 1)
    },
    showCursor() {
      return this.displayedLength < this.fullText.length
    },
    heroBgVars() {
      const desktop = `url(${this.backgroundImage})`
      const mobile = this.backgroundImageMobile
        ? `url(${this.backgroundImageMobile})`
        : desktop
      return {
        '--hero-bg-desktop': desktop,
        '--hero-bg-mobile': mobile,
      }
    },
    bgStyle() {
      return {
        transform: `translateY(${this.parallaxOffset}px)`,
      }
    },
  },
  watch: {
    title: { handler() { this.startTypewriter() }, immediate: false },
    titleGrey: { handler() { this.startTypewriter() }, immediate: false },
  },
  methods: {
    onScroll() {
      if (this.scrollTicking) return
      this.scrollTicking = true
      requestAnimationFrame(() => {
        const scrollY = window.scrollY || document.documentElement.scrollTop
        const factor = 0.35
        const maxOffset = 120
        this.parallaxOffset = Math.min(scrollY * factor, maxOffset)
        this.scrollTicking = false
      })
    },
    startTypewriter() {
      if (this.typewriterTimer) clearTimeout(this.typewriterTimer)
      this.displayedLength = 0
      this.type()
    },
    type() {
      if (this.displayedLength < this.fullText.length) {
        this.displayedLength++
        this.typewriterTimer = setTimeout(() => this.type(), this.typingSpeed)
      }
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
    this.startTypewriter()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    if (this.typewriterTimer) clearTimeout(this.typewriterTimer)
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";

#features-hero {
  position: relative;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  margin-top: -$mg-desktop;
  height: 100dvh;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  box-sizing: border-box;
  overflow: hidden;

  .hero-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: calc(100% - 80px);
    background-image: var(--hero-bg-desktop);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 0;
    will-change: transform;
    @media #{$mobile} {
      background-image: var(--hero-bg-mobile);
    }
  }

  .hero-gradient {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 40%;
    min-height: 400px;
    background: linear-gradient(to top, rgba(0, 0, 0, 1) 0%, transparent 100%);
    z-index: 1;
    pointer-events: none;
  }

  .hero-title-block {
    position: relative;
    z-index: 2;
  }

  .hero-title {
    margin: 0;
    font-size: clamp(2rem, 6vw, 4rem);
    line-height: 1.1;
    font-weight: 500;
    max-width: 800px;
  }

  .hero-title-white {
    color: $white;
  }

  .hero-title-grey {
    color: $grey;
  }

  .hero-title-html {
    color: $white;
  }

  .hero-title-html::v-deep .title-grey {
    color: $grey;
  }

  .hero-cursor {
    display: inline-block;
    animation: hero-cursor-blink 1s infinite;
    margin-left: 2px;
    color: $white;
  }

  @keyframes hero-cursor-blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }

  .arrow-block {
    position: relative;
    z-index: 2;
    padding: 2rem;
  }
}
</style>
